import * as React from "react";
import { createContext, useState, useEffect, useContext, useMemo } from "react";
import axios from "axios";
import { createAsyncCache } from "../util/createAsyncCache";
import { useErrorDialog } from "@tlc/ui/dialog/error/ErrorDialogContext";

export interface HostSystemSupportedFunctionality {
  // updateUserInformation: Patron;
  pinEditable: boolean;
  phoneNumberEditable: boolean;
  addressEditable: boolean;
  emailEditable: boolean;
  canPlaceItemRequests: boolean;
  canPlaceTitleRequests: boolean;
  supportsUserFunctionality: boolean;
  supportsAvailabilityFunctionality: boolean;
  supportsHoldingsCollection: boolean;
  supportsHoldingsCallNumber: boolean;
  supportsBookingsFunctionality: boolean;
  supportsSingleLogon: boolean;
  supportsLoanHistory: boolean;
  useGuardianProhibition: boolean; // prohibited lists
  includeGuardiansInAD: boolean;
  allowGuardianToViewDependentLoans: boolean;
  allowGuardianToViewDependentHolds: boolean;
  allowGuardianToViewDependentFines: boolean;
}

export interface SupportedFunctionalityContextValue {
  supportedFunctionality: HostSystemSupportedFunctionality | null;
}

export const SupportedFunctionalityHolder =
  createAsyncCache<HostSystemSupportedFunctionality>(async () => {
    const { data } = await axios.get<HostSystemSupportedFunctionality>(
      "/hostSystem/supportedFunctionality",
    );

    return data;
  });

export const SupportedFunctionalityContext =
  createContext<SupportedFunctionalityContextValue | null>(null);

export const SupportedFunctionalityProvider = ({
  children,
}: React.PropsWithChildren) => {
  const { handleRequestError } = useErrorDialog();
  const [supportedFunctionality, setSupportedFunctionality] =
    useState<HostSystemSupportedFunctionality | null>(
      SupportedFunctionalityHolder.current || null,
    );

  useEffect(() => {
    let cancelled = false;
    SupportedFunctionalityHolder.get()
      .then((data) => {
        if (!cancelled) {
          setSupportedFunctionality(data);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          handleRequestError(e);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [handleRequestError]);

  const contextValue = useMemo(
    () => ({
      supportedFunctionality,
    }),
    [supportedFunctionality],
  );

  return (
    <SupportedFunctionalityContext.Provider value={contextValue}>
      {children}
    </SupportedFunctionalityContext.Provider>
  );
};

export function useSupportedFunctionality() {
  const context = useContext(SupportedFunctionalityContext);

  if (!context) {
    throw new Error(
      "useSupportedFunctionality must be used within a SupportedFunctionalityProvider",
    );
  }

  return context;
}
